import { Fragment, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { previewBase, unitSymbol, useStockLocations, useStockMovements, type BatchRow, type ProductRow, type ProductVersionRow, type StockMovementRow, type SupplierRow } from '../../data/catalog.ts'
import { converterOptions, dayText, densityApplies, densityLabel, densityOf, expiryStatus, formulationPairs, SOON_DAYS, sortBatches, sortVersions } from '../../data/catalogueUi.ts'
import { buildReversePayload, canReverse, isNegative, locationLabel, movementLink, movementsOf, reasonProblem, reversedIds, STOCK_PROBLEM_KEY, stockReasonKey } from '../../data/stockUi.ts'
import { onHandByProduct } from '@gk/contracts'
import { fmtNum } from '../../lib/format.ts'
import { formatLocal } from '../../lib/time.ts'
import { PhotoStrip } from '../../photos/PhotoField.tsx'
import type { Pending } from '../../sync/merge.ts'
import { DisabledHint, Field, Pill, errorText } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { History } from '../../ui/History.tsx'
import { usePersonNames } from '../measures/shared.tsx'
import { FoldSummary } from '../workshop/shared.tsx'
import { BatchForm, EditProductForm, ReviseForm, VersionDocument } from './ProductForms.tsx'
import { ReceiveForm } from './ReceiveForm.tsx'
import { Feedback, useCatalogueAccess, useLang, useStockAccess } from './shared.tsx'

const sectionCls = 'flex flex-col gap-2 border-t-2 border-slate-200 pt-3 dark:border-slate-700'

/** What is behind a product row: its versions, its batches, the converter, and (for the storeroom and the workshop) the forms. `versions` and `batches` are this product's own. */
export function ProductDetail({ product, versions, batches, suppliers, supplierName, today, tz }: {
  product: ProductRow; versions: readonly ProductVersionRow[]; batches: readonly BatchRow[]; suppliers: readonly SupplierRow[]; supplierName: ReadonlyMap<string, string>; today: string; tz: string
}) {
  const { t } = useTranslation()
  const access = useCatalogueAccess()
  const stock = useStockAccess()
  const w = useWorkshopAction()
  const [mode, setMode] = useState<'edit' | 'revise' | 'batch' | 'receive' | null>(null)
  const sorted = useMemo(() => sortVersions(versions), [versions])
  const newest = sorted[0]
  const choose = (m: typeof mode) => { w.reset(); setMode(mode === m ? null : m) }
  return (
    <div className="flex flex-col gap-4" id={`prod-${product.id}`} data-testid="product-detail">
      <StockSection product={product} tz={tz} />
      <VersionsSection versions={sorted} tz={tz} />
      <BatchesSection batches={batches} supplierName={supplierName} today={today} />
      <Converter key={product.id} product={product} newest={newest} />
      {(access.allowed || stock.allowed) && (
        <div className={sectionCls} data-testid="product-actions">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {access.allowed && <button type="button" className={`btn !min-h-11 !px-3 ${mode === 'edit' ? 'btn-primary' : ''}`} aria-pressed={mode === 'edit'} onClick={() => choose('edit')} data-testid="product-edit">{t('catalogue.actions.edit')}</button>}
            {access.allowed && <button type="button" className={`btn !min-h-11 !px-3 ${mode === 'revise' ? 'btn-primary' : ''}`} aria-pressed={mode === 'revise'} disabled={!newest} onClick={() => choose('revise')} data-testid="product-revise">{t('catalogue.actions.revise')}</button>}
            {access.allowed && <button type="button" className={`btn !min-h-11 !px-3 ${mode === 'batch' ? 'btn-primary' : ''}`} aria-pressed={mode === 'batch'} onClick={() => choose('batch')} data-testid="batch-add">{t('catalogue.actions.addBatch')}</button>}
            {/* M15-A: a delivery of this product (the product is known: no search) */}
            {stock.allowed && !product.archived_at && <button type="button" className={`btn !min-h-11 !px-3 ${mode === 'receive' ? 'btn-primary' : ''}`} aria-pressed={mode === 'receive'} onClick={() => choose('receive')} data-testid="product-receive">{t('stock.receive')}</button>}
          </div>
          {mode === 'edit' && <EditProductForm product={product} suppliers={suppliers} w={w} access={access} onDone={() => setMode(null)} />}
          {mode === 'revise' && newest && <ReviseForm product={product} newest={newest} w={w} access={access} onDone={() => setMode(null)} />}
          {mode === 'batch' && <BatchForm product={product} lots={batches.map((b) => b.lot)} suppliers={suppliers} today={today} w={w} access={access} onDone={() => setMode(null)} />}
          {mode === 'receive' && <ReceiveForm product={product} />}
          <Feedback id="product" error={w.error} ok={w.ok} pending={w.pending} />
        </div>
      )}
      <History entityType='catalog.product' entityId={product.id} />
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- M15-A: the stock of this product
const SHOWN = 8
/** On hand (the twin's exact sum of the mirrored movements, per place), the club's minimum, and the movements newest first: the date, the reason, the quantity as
 *  typed and in the base unit, who, what it was for (the job, the order, the machine), the delivery note's photo; "Corriger" makes a reversal with a reason
 *  (stock.manage, online): the movement stays, an opposite one is added, never an edit (rule 3). */
function StockSection({ product, tz }: { product: ProductRow; tz: string }) {
  const { t } = useTranslation()
  const lang = useLang()
  const movements = useStockMovements(); const locations = useStockLocations()
  const mine = useMemo(() => movementsOf(movements ?? [], product.id), [movements, product.id])
  const sums = useMemo(() => onHandByProduct(mine).get(product.id), [mine, product.id])
  const reversed = useMemo(() => reversedIds(mine), [mine])
  const placeName = useMemo(() => new Map((locations ?? []).map((l) => [l.id, locationLabel(l, lang)])), [locations, lang])
  const [all, setAll] = useState(false)
  if (!movements || !locations) return null
  const unit = unitSymbol(product.base_unit_code, lang)
  const onHand = Number(sums?.total ?? 0)
  const min = product.min_qty_base === null || product.min_qty_base === undefined || product.min_qty_base === '' ? null : Number(product.min_qty_base)
  const low = min !== null && onHand < min
  const shown = all ? mine : mine.slice(0, SHOWN)
  return (
    <section className={sectionCls} data-testid="product-stock">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-bold">{t('stock.onHandLabel')}</h3>
        <span className="text-2xl font-bold tabular-nums" data-testid="product-on-hand">{fmtNum(onHand, lang, 3)} {unit}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {/* M15a ergonomics review: a sum below zero is an entry never typed (a receipt is missing): said here as on the Stock tab, not only a minus sign */}
        {isNegative(onHand) && <span data-testid="product-negative-pill"><Pill tone="red">{t('stock.negative')}</Pill></span>}
        {low && <span data-testid="product-low-pill"><Pill tone="amber">{t('stock.low', { min: fmtNum(min ?? 0, lang, 3), unit })}</Pill></span>}
        <span className="meta" data-testid="product-min">{min !== null ? t('stock.min', { min: fmtNum(min, lang, 3), unit }) : t('stock.noMin')}</span>
      </div>
      {sums && sums.byLocation.size > 1 && (
        <ul className="flex flex-col gap-1" data-testid="product-places">
          {[...sums.byLocation].map(([loc, qty]) => <li key={loc} className="flex justify-between gap-3 text-sm" data-location={loc}><span>{placeName.get(loc) ?? '…'}</span><span className="font-semibold tabular-nums">{fmtNum(Number(qty), lang, 3)} {unit}</span></li>)}
        </ul>
      )}
      <h4 className="font-semibold">{t('stock.movements.title')}</h4>
      {mine.length === 0 && <p className="meta" data-testid="movements-none">{t('stock.movements.none')}</p>}
      <ul className="flex flex-col gap-2" data-testid="movement-list">
        {shown.map((m) => <MovementLine key={m.id} m={m} product={product} tz={tz} lang={lang} placeName={placeName} reversed={reversed.has(m.id)} canCorrect={canReverse(m, reversed) && !m._pending} />)}
      </ul>
      {!all && mine.length > SHOWN && <button type="button" className="btn" onClick={() => setAll(true)} data-testid="movements-more">{t('stock.movements.more', { count: mine.length - SHOWN })}</button>}
    </section>
  )
}

function MovementLine({ m, product, tz, lang, placeName, reversed, canCorrect }: { m: Pending<StockMovementRow>; product: ProductRow; tz: string; lang: 'fr' | 'en'; placeName: ReadonlyMap<string, string>; reversed: boolean; canCorrect: boolean }) {
  const { t } = useTranslation()
  const stock = useStockAccess()
  const names = usePersonNames()
  const key = stockReasonKey(m.reason)
  const link = movementLink(m)
  const who = names(m.actor_person_id)
  const entered = `${fmtNum(Number(m.qty_entered), lang, 3)} ${unitSymbol(m.unit_entered, lang)}`
  const base = `${Number(m.qty_base) > 0 ? '+' : ''}${fmtNum(Number(m.qty_base), lang, 3)} ${unitSymbol(m.base_unit_code, lang)}`
  return (
    <li className={`flex flex-col gap-1 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600 ${reversed ? 'opacity-70' : ''}`} data-testid="movement-row" data-movement={m.id} data-reason={m.reason} data-reversed={reversed ? '1' : '0'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-bold" data-testid="movement-reason">{key ? t(key) : m.reason}</span>
        <span className="font-semibold tabular-nums" data-testid="movement-base">{base}</span>
      </div>
      <p className="meta">
        <span data-testid="movement-when">{formatLocal(m.occurred_at, tz, 'dd/MM/yyyy HH:mm')}</span> · <span data-testid="movement-entered">{entered}</span>
        {placeName.get(m.location_id) && <> · {placeName.get(m.location_id)}</>}
        {who && <> · {t('stock.movements.by', { name: who })}</>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {reversed && <Pill tone="gray">{t('stock.movements.reversed')}</Pill>}
        {m._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
        {link?.kind === 'job' && <Link to="/plan/job/$jobId" params={{ jobId: link.id }} className="btn !min-h-11 !px-3 text-sm" data-testid="movement-job">{t('stock.movements.job')}</Link>}
        {link?.kind === 'order' && <Link to="/workshop" className="btn !min-h-11 !px-3 text-sm" data-testid="movement-order">{t('stock.movements.order')}</Link>}
        {link?.kind === 'machine' && <Link to="/assets/$assetId" params={{ assetId: link.id }} className="btn !min-h-11 !px-3 text-sm" data-testid="movement-machine">{t('stock.movements.machine')}</Link>}
      </div>
      {m.note && <p className="text-sm">{m.note}</p>}
      {m.reason === 'receipt' && <PhotoStrip entityId={m.id} />}
      {stock.permitted && canCorrect && <ReverseForm movement={m} product={product} />}
    </li>
  )
}

/** "Corriger": the reversal of one movement, with its reason (required by the schema and by the server, STOCK_REASON_REQUIRED). */
function ReverseForm({ movement, product }: { movement: Pending<StockMovementRow>; product: ProductRow }) {
  const { t } = useTranslation()
  const stock = useStockAccess()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [reason, setReason] = useState('')
  const problem = reasonProblem(reason)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problem || !stock.canSave) return
    void w.run('stock.reverse@1', buildReversePayload(id.current, movement.id, reason), 'stock.movements.corrected', { aggregateId: product.id }).then((ok) => { if (ok) { id.current = newId(); setReason('') } })
  }
  return (
    <details className="group">
      <FoldSummary testId="movement-correct">{t('stock.movements.correct')}</FoldSummary>
      <form onSubmit={submit} noValidate className="flex flex-col gap-2" data-testid="reverse-form">
        <p className="meta">{t('stock.movements.correctHelp')}</p>
        <Field label={t('stock.movements.reasonLabel')} htmlFor={`rv-${movement.id}`}><input id={`rv-${movement.id}`} className="input" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reverse-reason" /></Field>
        <Feedback id="reverse" error={w.error} ok={w.ok} pending={w.pending} />
        <DisabledHint show={!!problem && stock.canSave} testId="reverse-hint">{problem ? t(STOCK_PROBLEM_KEY[problem]) : ''}</DisabledHint>
        <button className="btn !min-h-11" disabled={w.busy || !!problem || !stock.canSave} data-testid="reverse-go">{w.busy ? t('common.saving') : t('stock.movements.correctGo')}</button>
      </form>
    </details>
  )
}

function VersionsSection({ versions, tz }: { versions: readonly ProductVersionRow[]; tz: string }) {
  const { t } = useTranslation()
  const lang = useLang()
  return (
    <section className={sectionCls} data-testid="product-versions">
      <h3 className="text-lg font-bold">{t('catalogue.version.title')}</h3>
      <ul className="flex flex-col gap-2">
        {versions.map((v, i) => {
          const d = densityOf(v); const pairs = formulationPairs(v.formulation, lang)
          return (
            <li key={v.id} className="flex flex-col gap-1 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="version-row" data-version={v.id} data-version-no={v.version_no}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold">{t('catalogue.version.n', { n: v.version_no })}</span>
                {i === 0 && <Pill tone="green">{t('catalogue.version.current')}</Pill>}
                {v.created_at && <span className="meta">{formatLocal(v.created_at, tz, 'dd/MM/yyyy')}</span>}
              </div>
              <p className="text-sm">
                {d !== null ? <span className="font-semibold" data-testid="version-density">{densityLabel(d, lang)}</span> : <span className="opacity-70" data-testid="version-density">{t('catalogue.density.none')}</span>}
                {v.density_source && <span> · {t('catalogue.version.source', { source: v.density_source })}</span>}
              </p>
              {pairs.length > 0 && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm" data-testid="version-formulation">
                  {pairs.map((p) => <Fragment key={p.key}><dt className="font-semibold">{p.key}</dt><dd className="min-w-0 break-words">{p.value}</dd></Fragment>)}
                </dl>
              )}
              {/* M15-V: a label of Réglages › Documents shows its file to download; a text document keeps its page */}
              {v.document_version_id && (
                <VersionDocument documentVersionId={v.document_version_id} fallback={
                  <Link to="/procedures/$versionId" params={{ versionId: v.document_version_id }} className="inline-flex min-h-11 items-center self-start text-sm font-semibold underline" data-testid="version-sheet">{t('catalogue.version.sheet')}</Link>} />
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function BatchesSection({ batches, supplierName, today }: { batches: readonly BatchRow[]; supplierName: ReadonlyMap<string, string>; today: string }) {
  const { t } = useTranslation()
  const rows = useMemo(() => sortBatches(batches), [batches])
  return (
    <section className={sectionCls} data-testid="product-batches">
      <h3 className="text-lg font-bold">{t('catalogue.batch.title')}</h3>
      {rows.length === 0 && <p className="meta" data-testid="batch-none">{t('catalogue.batch.none')}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((b) => {
          const status = expiryStatus(b.expiry, today)
          const sup = b.supplier_id ? supplierName.get(b.supplier_id) : undefined
          return (
            <li key={b.id} className="flex flex-col gap-1 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="batch-row" data-batch={b.id}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="break-all font-bold" data-testid="batch-lot-text">{t('catalogue.batch.lotLabel', { lot: b.lot })}</span>
                <span data-testid="batch-expiry-pill" data-status={status}>
                  {status === 'expired' && <Pill tone="red">{t('catalogue.expiry.expired')}</Pill>}
                  {status === 'soon' && <Pill tone="amber">{t('catalogue.expiry.soon', { count: SOON_DAYS })}</Pill>}
                </span>
              </div>
              <p className="text-sm">{b.expiry ? t('catalogue.batch.expiresOn', { date: dayText(b.expiry) }) : <span className="opacity-70">{t('catalogue.batch.noExpiry')}</span>}</p>
              {b.received_on && <p className="meta">{t('catalogue.batch.receivedLabel', { date: dayText(b.received_on) })}</p>}
              {sup && <p className="meta">{t('catalogue.batch.supplierLabel', { name: sup })}</p>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** "How much is that in the product's own unit?" A display aid: the server converts again when a part is recorded. The units offered are the ones the server would accept for this product. */
function Converter({ product, newest }: { product: ProductRow; newest: ProductVersionRow | undefined }) {
  const { t } = useTranslation()
  const lang = useLang()
  const density = densityOf(newest)
  const options = useMemo(() => converterOptions(product.base_unit_code, density !== null), [product.base_unit_code, density])
  const [value, setValue] = useState('')
  const [from, setFrom] = useState(product.base_unit_code)
  const unit = options.some((o) => o.unit.code === from) ? from : product.base_unit_code
  const result = value.trim() === '' ? null : previewBase(value, unit, product, newest)
  const needsDensity = density === null && densityApplies(product.base_unit_code)           // a mass or a volume with no density: the other cannot be offered
  const base = unitSymbol(product.base_unit_code, lang)
  return (
    <section className={sectionCls} data-testid="product-converter">
      <h3 className="text-lg font-bold">{t('catalogue.convert.title')}</h3>
      <p className="meta">{t('catalogue.convert.note')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('catalogue.convert.value')} htmlFor={`cv-${product.id}`}>
          <input id={`cv-${product.id}`} className="input" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} data-testid="convert-value" />
        </Field>
        <Field label={t('catalogue.convert.from')} htmlFor={`cf-${product.id}`}>
          <select id={`cf-${product.id}`} className="input" value={unit} onChange={(e) => setFrom(e.target.value)} data-testid="convert-from">
            {options.map((o) => <option key={o.unit.code} value={o.unit.code}>{o.viaDensity ? t('catalogue.convert.viaDensity', { unit: unitSymbol(o.unit.code, lang) }) : unitSymbol(o.unit.code, lang)}</option>)}
          </select>
        </Field>
      </div>
      <p role="status" aria-live="polite" className={`min-h-6 text-base font-semibold ${result && !result.ok ? 'text-red-700 dark:text-red-400' : ''}`} data-testid="convert-result" data-state={result === null ? 'empty' : result.ok ? 'ok' : 'refused'}>
        {result === null && <span className="font-normal opacity-70">{t('catalogue.convert.prompt', { unit: base })}</span>}
        {result?.ok && t('catalogue.convert.equals', { value: fmtNum(Number(result.qtyBase), lang, 6), unit: base })}
        {result && !result.ok && errorText(t, result.code)}
      </p>
      {needsDensity && <p className="meta" data-testid="convert-hint">{t('catalogue.convert.noDensityHint')}</p>}
    </section>
  )
}
